import type { ElementRef } from '@angular/core';
import type { FormGroup } from '@angular/forms';

/**
 * Focuses the first invalid form control's DOM element.
 *
 * Iterates through the supplied `ElementRef` list in **DOM order** (as returned
 * by `viewChildren`), checks whether the corresponding form control is invalid,
 * and focuses the first match. This ensures focus always moves to the topmost
 * visible invalid field on the page, regardless of `FormGroup` declaration order.
 *
 * @param form - The reactive `FormGroup` to inspect.
 * @param inputs - A list of `ElementRef<HTMLElement>` from `viewChildren('formInput')`.
 *
 * @example
 * ```ts
 * readonly formInputs = viewChildren<ElementRef<HTMLElement>>('formInput');
 *
 * onSubmit(): void {
 *   this.form.markAllAsTouched();
 *   if (this.form.invalid) {
 *     focusFirstInvalidField(this.form, this.formInputs());
 *     return;
 *   }
 * }
 * ```
 */
export function focusFirstInvalidField(
  form: FormGroup,
  inputs: readonly unknown[],
): void {
  for (const ref of inputs) {
    const element = (ref as ElementRef<HTMLElement> | undefined)?.nativeElement;
    if (!element) {
      // `#formInput` on a component yields the component instance (no nativeElement).
      const componentRef = ref as {
        focus?: () => void;
        isInvalid?: () => boolean;
      };

      const isFocusableFieldComponent =
        typeof componentRef.focus === 'function' &&
        typeof componentRef.isInvalid === 'function';

      if (isFocusableFieldComponent && componentRef.isInvalid?.()) {
        requestAnimationFrame(() => {
          componentRef.focus?.();
        });
        return;
      }

      continue;
    }

    const key = element.getAttribute('formcontrolname') ?? element.id;

    if (!key) continue;

    if (form.get(key)?.invalid) {
      const target =
        (element.querySelector('input') as HTMLElement | null) ?? element;

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });

      requestAnimationFrame(() => {
        target.focus({ preventScroll: true });
      });
      return;
    }
  }
}
